import { requireSupabase } from "./supabaseClient";
import { uploadPrivateDocument } from "./documentService";
import type { DocumentTarget } from "./documentService";

export type WalkdownVerification = "verified" | "needs_research" | "incomplete";

export type WalkdownLine = {
  id: string;
  plant_id: string;
  line_number: number;
  name: string;
  description: string | null;
  completeness: number;
  display_order: number;
};

export type WalkdownAsset = {
  id: string;
  plant_id: string;
  line_id: string;
  parent_asset_id: string | null;
  asset_tag: string;
  display_order: number;
  name: string;
  equipment_type: string;
  manufacturer: string | null;
  model: string | null;
  serial_number: string | null;
  criticality: "A" | "B" | "C";
  verification_state: WalkdownVerification;
  completeness: number;
  notes: string | null;
};

export type WalkdownInspection = {
  id: string;
  asset_id: string;
  inspected_at: string;
  inspected_by: string | null;
  result: string | null;
  notes: string | null;
  measurements: Record<string, unknown>;
};

export type WalkdownSnapshot = {
  lines: WalkdownLine[];
  assets: WalkdownAsset[];
  latestInspectionByAsset: Record<string, WalkdownInspection>;
};

async function rpc<T>(name: string, args: Record<string, unknown>): Promise<T> {
  const client = requireSupabase();
  const { data, error } = await client.rpc(name, args);
  if (error) throw error;
  return data as T;
}

export async function loadWalkdownSnapshot(): Promise<WalkdownSnapshot> {
  const client = requireSupabase();
  const [linesRes, assetsRes, inspectionsRes] = await Promise.all([
    client.from("lines").select("id,plant_id,line_number,name,description,completeness,display_order,is_active").eq("is_active", true).order("display_order"),
    client.from("assets").select("id,plant_id,line_id,parent_asset_id,asset_tag,display_order,name,equipment_type,manufacturer,model,serial_number,criticality,verification_state,completeness,notes,status").neq("status", "retired").order("display_order"),
    client.from("inspections").select("id,asset_id,inspected_at,inspected_by,result,notes,measurements,inspection_type").eq("inspection_type", "plant_walkdown").order("inspected_at", { ascending: false }).limit(1000),
  ]);
  const error = linesRes.error ?? assetsRes.error ?? inspectionsRes.error;
  if (error) throw error;

  const latestInspectionByAsset: Record<string, WalkdownInspection> = {};
  for (const row of inspectionsRes.data ?? []) {
    if (!latestInspectionByAsset[row.asset_id]) {
      latestInspectionByAsset[row.asset_id] = {
        id: row.id,
        asset_id: row.asset_id,
        inspected_at: row.inspected_at,
        inspected_by: row.inspected_by,
        result: row.result,
        notes: row.notes,
        measurements: (row.measurements ?? {}) as Record<string, unknown>,
      };
    }
  }

  return {
    lines: (linesRes.data ?? []).map((row) => ({
      id: row.id,
      plant_id: row.plant_id,
      line_number: row.line_number,
      name: row.name,
      description: row.description,
      completeness: row.completeness,
      display_order: row.display_order,
    })) as WalkdownLine[],
    assets: (assetsRes.data ?? []).filter((row) => Boolean(row.line_id)).map((row) => ({
      id: row.id,
      plant_id: row.plant_id,
      line_id: row.line_id as string,
      parent_asset_id: row.parent_asset_id,
      asset_tag: row.asset_tag,
      display_order: row.display_order,
      name: row.name,
      equipment_type: row.equipment_type,
      manufacturer: row.manufacturer,
      model: row.model,
      serial_number: row.serial_number,
      criticality: row.criticality,
      verification_state: row.verification_state as WalkdownVerification,
      completeness: row.completeness,
      notes: row.notes,
    })) as WalkdownAsset[],
    latestInspectionByAsset,
  };
}

export async function addWalkdownAsset(input: {
  lineId: string;
  parentAssetId?: string | null;
  assetTag: string;
  name: string;
  equipmentType: string;
  manufacturer?: string;
  model?: string;
  serialNumber?: string;
  criticality?: "A" | "B" | "C";
  notes?: string;
}) {
  return rpc<string>("add_walkdown_asset", {
    p_line_id: input.lineId,
    p_parent_asset_id: input.parentAssetId ?? null,
    p_asset_tag: input.assetTag,
    p_name: input.name,
    p_equipment_type: input.equipmentType,
    p_manufacturer: input.manufacturer ?? "",
    p_model: input.model ?? "",
    p_serial_number: input.serialNumber ?? "",
    p_criticality: input.criticality ?? "B",
    p_notes: input.notes ?? "",
  });
}

export async function submitWalkdownInspection(input: {
  assetId: string;
  manufacturer: string;
  model: string;
  serialNumber: string;
  verificationState: WalkdownVerification;
  completeness: number;
  result: string;
  notes: string;
  measurements?: Record<string, unknown>;
}) {
  return rpc<string>("submit_walkdown_inspection", {
    p_asset_id: input.assetId,
    p_manufacturer: input.manufacturer,
    p_model: input.model,
    p_serial_number: input.serialNumber,
    p_verification_state: input.verificationState,
    p_completeness: input.completeness,
    p_result: input.result,
    p_notes: input.notes,
    p_measurements: input.measurements ?? {},
  });
}

export async function uploadWalkdownPhoto(asset: WalkdownAsset, file: File, kind: "nameplate_photo" | "equipment_photo") {
  const target: DocumentTarget = {
    kind: "asset",
    id: asset.id,
    plantId: asset.plant_id,
    lineId: asset.line_id,
    label: `${asset.asset_tag} · ${asset.name}`,
  };
  return uploadPrivateDocument({
    target,
    documentType: kind,
    title: kind === "nameplate_photo" ? `${asset.asset_tag} Nameplate` : `${asset.asset_tag} Equipment Photo`,
    notes: "Captured during plant walkdown",
    file,
  });
}

export async function currentWalkdownRole() {
  const client = requireSupabase();
  const { data: authData, error: authError } = await client.auth.getUser();
  if (authError) throw authError;
  if (!authData.user) return "viewer";
  const { data, error } = await client.from("profiles").select("role").eq("user_id", authData.user.id).maybeSingle();
  if (error) throw error;
  return data?.role ?? "viewer";
}
